import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Download, CheckCircle2, XCircle, FileText, AlertCircle, Loader2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { downloadCsv } from '@/utils/reportCsv';
import { buildPostingLogCsv } from '@/utils/PostingLog/csv';
import { resolvePostingLogWeekEnding } from '@/utils/PostingLog/weekEnding';
import { pivotPostingLogLines } from '@/utils/PostingLog/pivot';
import { getLineDisplayStatus } from '@/utils/PostingLog/mappers';
import { usePostingLogLines } from '@/hooks/PostingLog/usePostingLogLines';
import { useSettings } from '@/contexts/SettingsContext';
import { LineTable } from './LineTable';
import { PivotedLineGrid } from './PivotedLineGrid';

interface DrilldownPanelProps {
  timesheetId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allowRetry: boolean;
  allowExport: boolean;
  /** Incremented after a retry so the panel reloads its lines. */
  refreshKey: number;
  retryingIds: Set<string>;
  /** When set, Retry is disabled with this reason. */
  retryDisabledReason?: string;
  onBulkRetry: (logIds: number[]) => void;
}

export const DrilldownPanel: React.FC<DrilldownPanelProps> = ({
  timesheetId, open, onOpenChange, allowRetry, allowExport,
  refreshKey, retryingIds, retryDisabledReason, onBulkRetry,
}) => {
  const { lines, isLoading, loadError } = usePostingLogLines(timesheetId, open, refreshKey);
  const { weekStartDay } = useSettings();

  const employee   = lines[0]?.employeeName ?? '';
  const weekEnding = useMemo(() => resolvePostingLogWeekEnding(lines, weekStartDay), [lines, weekStartDay]);
  const pivotedGroups = useMemo(
    () => pivotPostingLogLines(lines, weekEnding, weekStartDay),
    [lines, weekEnding, weekStartDay],
  );

  const totalLines  = lines.length;
  const postedLines = lines.filter((l) => l.status === 'Posted').length;
  const failedLogIds = lines.filter((l) => l.status === 'Failed').map((l) => l.logId);
  const failedLines = lines.filter((l) => getLineDisplayStatus(l) === 'Failed').length;
  const notPostedLines = failedLogIds.length - failedLines;
  const isRetrying = retryingIds.size > 0;

  const bannerVariant =
    failedLogIds.length === 0 ? 'success' : postedLines === 0 ? 'error' : 'partial';

  const bannerClass = {
    success: 'bg-success-50 border-success-200 text-success-800 dark:bg-success-900/20 dark:border-success-800 dark:text-success-300',
    error:   'bg-error-50 border-error-200 text-error-800 dark:bg-error-900/20 dark:border-error-800 dark:text-error-300',
    partial: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300',
  }[bannerVariant];

  const BannerIcon = {
    success: CheckCircle2,
    error:   XCircle,
    partial: AlertCircle,
  }[bannerVariant];

  const handleExport = () => {
    try {
      downloadCsv(buildPostingLogCsv(lines), `posting-log-${timesheetId}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      toast.success('Export successful', { description: `Exported ${lines.length} lines.` });
    } catch {
      toast.error('Export failed');
    }
  };

  if (!timesheetId) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-full flex flex-col p-0 gap-0 sm:w-[min(96vw,1400px)]"
      >
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <SheetTitle className="text-base font-semibold leading-tight">
                Posting Log — Timesheet #{timesheetId}
              </SheetTitle>
              <p className="text-sm text-muted-foreground">
                {employee}
                {weekEnding ? ` · w/e ${format(weekEnding, 'dd/MM/yyyy')}` : ''}
              </p>
            </div>
            {allowExport && lines.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleExport} className="flex-shrink-0 gap-1.5">
                <Download className="h-3.5 w-3.5" />
                Export CSV
              </Button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full [&>div]:!block">
            <div className="px-6 py-5 space-y-5">
              {/* Summary banner */}
              {lines.length > 0 && (
                <div className={cn('flex items-start gap-2 rounded-md border p-3 text-sm', bannerClass)}>
                  <BannerIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="flex-1">
                    {postedLines} of {totalLines} line{totalLines !== 1 ? 's' : ''} posted successfully
                    {failedLogIds.length > 0 && ` — ${[
                      failedLines > 0 && `${failedLines} failed`,
                      notPostedLines > 0 && `${notPostedLines} not posted`,
                    ].filter(Boolean).join(', ')}`}
                  </span>
                  {allowRetry && failedLogIds.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-shrink-0 gap-1.5"
                      onClick={() => onBulkRetry(failedLogIds)}
                      disabled={!!retryDisabledReason || isLoading || isRetrying}
                      title={retryDisabledReason}
                    >
                      {isRetrying
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RotateCcw className="h-3.5 w-3.5" />}
                      Retry
                    </Button>
                  )}
                </div>
              )}

              {lines.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                  {isLoading ? (
                    <Loader2 className="h-8 w-8 opacity-30 animate-spin" />
                  ) : loadError ? (
                    <>
                      <XCircle className="h-8 w-8 opacity-30" />
                      <p className="text-sm">Failed to load lines: {loadError}</p>
                    </>
                  ) : (
                    <>
                      <FileText className="h-8 w-8 opacity-30" />
                      <p className="text-sm">No lines found for this timesheet.</p>
                    </>
                  )}
                </div>
              ) : (
                <TooltipProvider>
                  {pivotedGroups ? (
                    <PivotedLineGrid groups={pivotedGroups} retryingIds={retryingIds} />
                  ) : (
                    <LineTable
                      lines={lines}
                      retryingIds={retryingIds}
                      sortKey="transactionDate"
                      sortDir="asc"
                      variant="drilldown"
                      compact
                    />
                  )}
                </TooltipProvider>
              )}
            </div>
          </ScrollAreaViewport>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
